import type { Options as OpenReplayNetworkOptions } from '@openreplay/tracker/lib/modules/network'
import { HTTP_HEADERS } from 'aven_types'

const SENSITIVE_HEADER_NAMES = [
    'Authorization',
    'SessionAuthorization',
    'CoApplicantAuthorization',
    'NotaryAuthorization',
    'SupportVideoAuthorization',
    HTTP_HEADERS.HIL_MERCHANT_APPLICATION_RESUME_TOKEN,
    'Cookie',
    'Set-Cookie',
]

// OpenReplay matches ignoreHeaders case-sensitively, so every casing our apps send has to be listed
export const OPENREPLAY_IGNORED_NETWORK_HEADERS: string[] = [...new Set(SENSITIVE_HEADER_NAMES.flatMap((headerName) => [headerName, headerName.toLowerCase()]))]

const SENSITIVE_HEADER_NAME_PATTERN = /authorization|token|cookie/i

interface OpenReplayNetworkCaptureHeaders {
    headers: Record<string, string>
}

interface OpenReplayNetworkCapture {
    request: OpenReplayNetworkCaptureHeaders
    response: OpenReplayNetworkCaptureHeaders
}

const omitSensitiveHeaders = (headers: Record<string, string>): Record<string, string> =>
    Object.fromEntries(Object.entries(headers).filter(([headerName]) => !SENSITIVE_HEADER_NAME_PATTERN.test(headerName)))

export const stripSensitiveHeadersFromOpenReplayNetworkCapture = <Capture extends OpenReplayNetworkCapture>(capture: Capture): Capture => ({
    ...capture,
    request: { ...capture.request, headers: omitSensitiveHeaders(capture.request.headers) },
    response: { ...capture.response, headers: omitSensitiveHeaders(capture.response.headers) },
})

interface OpenReplayNetworkCaptureWithBody extends OpenReplayNetworkCapture {
    status: number
    method: string
    url: string
    request: OpenReplayNetworkCaptureHeaders & { body?: unknown }
    response: OpenReplayNetworkCaptureHeaders & { body?: unknown }
}

const JODL_CREDENTIAL_FIELDS = new Set([
    'jwt',
    'jwtToken',
    'sourceJwt',
    'accessJWT',
    'refreshJWT',
    'sessionAccessJWT',
    'notaryAccessJWT',
    'autoSessionAccessJWT',
    'avenMyLoginJwtToken',
    'avenMyLoginJwtRefreshToken',
    'jwtTokens',
    'coApplicantJwtTokens',
    'thirdPartyLeadDryrunJwtTokens',
    'previousJwt',
    'pendingReturnJwt',
    'pendingReturnJwtPath',
    'jodlLegacyDestinationContexts',
    'authenticatedNotaryReturnData',
    'pendingNotaryReturnSourceJwt',
    'returnCredential',
    'returnToken',
    'returnToken2',
    'primaryReturnToken',
    'primaryReturnToken2',
    'destinationUrl',
])

const JODL_CREDENTIAL_QUERY_PARAMETERS = ['jwt', 'sourceJwt', 'returnCredential', 'returnToken', 'returnToken2', 'primaryReturnToken', 'primaryReturnToken2', 'a', 'p']

const redactDestinationReference = (destination: string): string => (destination.includes(':') ? destination.split(':', 1)[0] : destination)

export const sanitizeJodlCredentialQueryParams = ({ search, removeDestination = false }: { search: string; removeDestination?: boolean }): string => {
    const originalQuery = search.startsWith('?') ? search.slice(1) : search
    const queryParams = new URLSearchParams(originalQuery)
    const destination = queryParams.get('destination')
    const hasOpaqueDestination = destination?.includes(':') ?? false
    const hasCredential = JODL_CREDENTIAL_QUERY_PARAMETERS.some((key) => queryParams.has(key))
    const shouldRemoveDestination = removeDestination && queryParams.has('destination')
    if (!hasCredential && !hasOpaqueDestination && !shouldRemoveDestination) {
        return originalQuery
    }

    for (const key of JODL_CREDENTIAL_QUERY_PARAMETERS) {
        queryParams.delete(key)
    }
    if (hasOpaqueDestination && destination) {
        queryParams.set('destination', redactDestinationReference(destination))
    }
    if (removeDestination) {
        queryParams.delete('destination')
    }
    return queryParams.toString()
}

const redactJodlCredentialUrl = (value: string): string => {
    try {
        const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
        if (!isAbsolute && !value.startsWith('/')) {
            return value
        }
        const url = new URL(value, 'https://openreplay.invalid')
        const search = sanitizeJodlCredentialQueryParams({ search: url.search })
        if (search === url.search.slice(1)) {
            return value
        }
        url.search = search
        return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
    } catch {
        return value
    }
}

const isJsonObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const redactJodlCredentialValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
        if (/^\s*[[{]/.test(value)) {
            try {
                return JSON.stringify(redactJodlCredentialValue(JSON.parse(value)))
            } catch {
                return redactJodlCredentialUrl(value)
            }
        }
        return redactJodlCredentialUrl(value)
    }
    if (!isJsonObject(value)) {
        return value
    }
    const prototype = Object.getPrototypeOf(value)
    if (prototype !== Object.prototype && prototype !== null) {
        return value
    }
    return Object.fromEntries(
        Object.entries(value)
            .filter(([key]) => !JODL_CREDENTIAL_FIELDS.has(key.replace(/^(?:app)?(?:session|local)storage_/i, '')))
            .map(([key, nested]) => [key, key === 'destination' && typeof nested === 'string' ? redactDestinationReference(nested) : redactJodlCredentialValue(nested)]),
    )
}

/** Redacts return credentials and saved business URLs from network captures. */
export const redactJodlCredentialsFromOpenReplayNetworkCapture = <Capture extends OpenReplayNetworkCaptureWithBody>(capture: Capture): Capture => {
    const withoutSensitiveHeaders = stripSensitiveHeadersFromOpenReplayNetworkCapture(capture)

    return {
        ...withoutSensitiveHeaders,
        url: redactJodlCredentialUrl(withoutSensitiveHeaders.url),
        request: { ...withoutSensitiveHeaders.request, body: redactJodlCredentialValue(withoutSensitiveHeaders.request.body) },
        response: { ...withoutSensitiveHeaders.response, body: redactJodlCredentialValue(withoutSensitiveHeaders.response.body) },
    }
}

export const OPENREPLAY_REDACTED_NETWORK_OPTIONS: OpenReplayNetworkOptions = {
    sessionTokenHeader: false,
    failuresOnly: false,
    capturePayload: false,
    captureInIframes: true,
    ignoreHeaders: OPENREPLAY_IGNORED_NETWORK_HEADERS,
    sanitizer: stripSensitiveHeadersFromOpenReplayNetworkCapture,
}
